import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, isNetworkError } from "../api";
import { useReconnect } from "../hooks";
import { ErrorBanner, Skeleton } from "../components";
import { ALERTS_CHANGED_EVENT, SEVERITIES, alertPath, collapseTimeline, deliverySentence, filterAlerts, typeFilterOptions, splitOnLabel, relativeAge, severityInfo } from "../alerts";
import { Modal, ProviderIcon, SeverityBadge } from "./AlertSettings";

// The Alerts page: what is happening. An inbox of open problems and their
// history, system and personal alerts in one list. How anyone is told about
// them lives in Settings (personal) and Admin (system), see AlertSettings.jsx.

const TABS = [
  { key: "inbox", label: "Inbox" },
  { key: "history", label: "History" },
];

// pollMs refreshes the list; slowMs is how long a detail fetch may take before
// the modal opens with a skeleton instead of waiting.
const pollMs = 15000;
const slowMs = 300;

// ---- Inbox / History -------------------------------------------------------

const AlertRow = ({ a, onOpen, showAudience }) => (
  <button type="button" className={"alert-row" + (a.dismissed ? " alert-row-dismissed" : "") + (a.status === "resolved" ? " alert-row-resolved" : "")} onClick={() => onOpen(a)}>
    <div className="alert-row-main">
      <SeverityBadge severity={a.severity} />
      <span className="alert-row-title">{a.title}</span>
      {showAudience && <span className="badge">{a.audience_kind === "system" ? "System" : "My apps"}</span>}
      {a.status === "resolved" && <span className="badge">{a.resolve_reason === "removed" ? "Removed" : "Resolved"}</span>}
      {a.paused && a.status !== "resolved" && <span className="badge badge-warn">Notifications paused</span>}
      {a.dismissed && a.status !== "resolved" && <span className="badge">Dismissed</span>}
    </div>
    <div className="alert-row-meta">
      <span>{a.type_label}</span>
      <span className="alert-row-sep" aria-hidden="true">&middot;</span>
      <span>{a.resource_label}</span>
      <span className="alert-row-sep" aria-hidden="true">&middot;</span>
      <span title={new Date(a.status === "resolved" && a.resolved_at ? a.resolved_at : a.last_seen).toLocaleString()}>
        {a.status === "resolved" ? `resolved ${relativeAge(a.resolved_at)}` : `since ${relativeAge(a.first_seen)}`}
      </span>
    </div>
  </button>
);

// A bell with a check: nothing is ringing
const AllClearIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 44V29a14 14 0 0 1 28 0v15l4 5H14z" />
    <path d="M27 49a5 5 0 0 0 10 0" />
    <path d="m25 33 5 5 9-10" />
  </svg>
);

const AlertList = ({ all, types, history, admin, onOpen }) => {
  const [scope, setScope] = useState("");
  const [severity, setSeverity] = useState("");
  const [type, setType] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  // One fetch serves both tabs: the inbox is the open subset of the history
  const items = all === null ? null : history ? all : all.filter((a) => a.status !== "resolved");
  const filtered = filterAlerts(items, { scope, severity, type, showDismissed: history || showDismissed });
  const filtering = scope || severity || type || (!history && showDismissed);
  const clear = () => {
    setScope("");
    setSeverity("");
    setType("");
    setShowDismissed(false);
  };
  if (items === null) return <Skeleton rows={3} label="Loading alerts..." />;
  return (
    <>
      <div className="alert-filters">
        {admin && (
          <select className="settings-input alert-filter" value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Filter by scope">
            <option value="">System and my apps</option>
            <option value="system">System only</option>
            <option value="user">My apps only</option>
          </select>
        )}
        <select className="settings-input alert-filter" value={severity} onChange={(e) => setSeverity(e.target.value)} aria-label="Filter by severity">
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select className="settings-input alert-filter" value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by type">
          <option value="">All types</option>
          {typeFilterOptions(types, scope).map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        {!history && (
          <span className="alert-filter-check">
            <button type="button" role="switch" aria-checked={showDismissed} className={"conn-switch" + (showDismissed ? " on" : "")} onClick={() => setShowDismissed(!showDismissed)} aria-labelledby="alert-show-dismissed">
              <span className="conn-switch-knob" />
            </button>
            <span id="alert-show-dismissed" onClick={() => setShowDismissed(!showDismissed)}>Show dismissed</span>
          </span>
        )}
        {filtering && (
          <button type="button" className="btn btn-small" onClick={clear}>Clear filters</button>
        )}
      </div>
      {filtered.length === 0 && items.length === 0 && (
        <div className="alert-empty-hero">
          <div className="alert-empty-icon" aria-hidden="true">
            <AllClearIcon />
          </div>
          <h2>{history ? "No alerts yet" : "All clear"}</h2>
          <p>{history ? "Resolved alerts stay here for 90 days." : "Checks run automatically. Anything that needs attention shows up here, and at your destinations."}</p>
        </div>
      )}
      {filtered.length === 0 && items.length > 0 && (
        <div className="alert-empty">
          <p className="alert-empty-title">Nothing matches these filters.</p>
          <button type="button" className="btn btn-small" onClick={clear}>Clear filters</button>
        </div>
      )}
      {filtered.length > 0 && (
        <div className="alert-list">
          {filtered.map((a) => (
            <AlertRow key={a.id} a={a} onOpen={onOpen} showAudience={admin} />
          ))}
        </div>
      )}
    </>
  );
};

// ---- Detail -----------------------------------------------------------------

const eventLabel = (e) => {
  switch (e.kind) {
    case "opened":
      return `Opened as ${severityInfo(e.severity).label.toLowerCase()}`;
    case "escalated":
      return `Escalated to ${severityInfo(e.severity).label.toLowerCase()}`;
    case "downgraded":
      return `Lowered to ${severityInfo(e.severity).label.toLowerCase()}`;
    case "resolved":
      return e.detail === "removed" ? "Closed: the resource was removed" : "Resolved";
    case "resumed":
      return "Pause ended: notified again that it is still open";
    default:
      return e.kind;
  }
};

// The dialog waits for the full alert (or slowMs, then shows a skeleton), so
// it opens at its final size instead of growing as the timeline arrives.
const AlertDetail = ({ id, canAct, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setDetail(await api.get(alertPath(id)));
    } catch (err) {
      setError(err.message);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  // Open only once the whole alert is here: growing the modal as the timeline
  // arrives is the flicker. A slow fetch still gets a skeleton after a moment.
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), slowMs);
    return () => clearTimeout(timer);
  }, []);
  const act = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const footer = detail && (
    <div className="btn-row">
      <span style={{ flex: 1 }} />
      {canAct && detail.status !== "resolved" && !detail.dismissed && (
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(() => api.put(alertPath(id, "/dismissal")))} title="Hides it from the inbox for everyone until it gets worse; notifications are unaffected">
          Dismiss
        </button>
      )}
      {canAct && detail.status !== "resolved" && detail.dismissed && (
        <button type="button" className="btn" disabled={busy} onClick={() => act(() => api.del(alertPath(id, "/dismissal")))}>
          Undo dismissal
        </button>
      )}
      <button type="button" className="btn" onClick={onClose}>Close</button>
    </div>
  );
  if (!detail && !error && !slow) {
    return null;
  }
  return (
    <Modal title={detail ? detail.title : "Alert"} wide footer={footer} onClose={onClose}>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      {!detail && !error && <Skeleton rows={3} label="Loading alert..." />}
      {detail && (
        <div className="alert-detail">
          <div className="alert-detail-head">
            <SeverityBadge severity={detail.severity} />
            {detail.status === "resolved" ? <span className="badge">{detail.resolve_reason === "removed" ? "Removed" : "Resolved"}</span> : <span className="badge badge-accent">Open</span>}
            {detail.paused && detail.status !== "resolved" && <span className="badge badge-warn">Notifications paused</span>}
            {detail.dismissed && detail.status !== "resolved" && <span className="badge" title={detail.dismissed_by ? `Dismissed by ${detail.dismissed_by}` : undefined}>Dismissed</span>}
            <span className="alert-detail-type">{detail.type_label} &middot; {detail.resource_label}</span>
          </div>
          {(() => {
            // The resource's name in the message is its link; a message that
            // does not name it keeps a separate link below
            const parts = detail.link ? splitOnLabel(detail.message, detail.resource_label) : null;
            return (
              <>
                <p className="alert-detail-message">
                  {parts ? <>{parts[0]}<Link to={detail.link} onClick={onClose}>{parts[1]}</Link>{parts[2]}</> : detail.message}
                </p>
                {detail.link && !parts && (
                  <p className="hint">
                    <Link to={detail.link} onClick={onClose}>Open {detail.resource_label}</Link>
                  </p>
                )}
              </>
            );
          })()}
          <h3>Timeline</h3>
          <ul className="alert-timeline">
            {collapseTimeline(detail.events || []).reverse().map((e) => (
              <li key={e.id}>
                <span className="alert-timeline-when" title={new Date(e.created_at).toLocaleString()}>{relativeAge(e.created_at)}</span>
                <span>{eventLabel(e)}{e.count > 1 && <span className="cell-muted"> ({e.count} times)</span>}</span>
              </li>
            ))}
          </ul>
          <h3>Notifications</h3>
          {detail.deliveries && detail.deliveries.length === 0 && <p className="hint">Nobody was notified: no destination matched this alert, or none is set up.</p>}
          {detail.deliveries && detail.deliveries.length > 0 && (
            <ul className="alert-timeline alert-deliveries">
              {[...detail.deliveries].reverse().map((d, i) => {
                const s = deliverySentence(d);
                return (
                  <li key={i}>
                    <span className="alert-timeline-when" title={new Date(d.updated_at).toLocaleString()}>{relativeAge(d.updated_at)}</span>
                    <span className={`alert-delivery alert-delivery-${s.tone}`}>
                      <ProviderIcon provider={d.provider} />
                      <span>
                        {s.before}<b>{s.name}</b>{s.after}
                        <span className="cell-muted"> &middot; {s.about}</span>
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

// ---- The page ----------------------------------------------------------------

const Alerts = ({ account }) => {
  const [params, setParams] = useSearchParams();
  const admin = account.role === "admin";
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "inbox";
  const [all, setAll] = useState(null);
  const [types, setTypes] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(params.get("alert") ? { id: params.get("alert") } : null);
  const canAct = !!account.email; // a signed-in person (never the bare admin token)

  const setTab = (next) => {
    const p = new URLSearchParams(params);
    if (next === "inbox") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  };

  // Everything the viewer may see, in one fetch (history includes the open
  // ones), plus the type catalogs for both audiences; polled while open. The
  // data is kept across tab switches so the tabs do not flash a skeleton.
  const load = useCallback(async () => {
    try {
      const scopes = admin ? ["system", "mine"] : ["mine"];
      const [list, ...catalogs] = await Promise.all([api.get("/api/alerts?view=history"), ...scopes.map((sc) => api.get(`/api/alert-types?scope=${sc}`))]);
      setAll(list);
      setTypes(catalogs.flat());
    } catch (err) {
      if (!isNetworkError(err)) setError(err.message);
    }
  }, [admin]);
  useEffect(() => {
    load();
    const timer = setInterval(load, pollMs);
    return () => clearInterval(timer);
  }, [load]);
  useReconnect(load);
  // Drop ?alert= on close, so a reload does not reopen the detail
  const closeDetail = () => {
    setOpen(null);
    if (params.has("alert")) {
      const p = new URLSearchParams(params);
      p.delete("alert");
      setParams(p, { replace: true });
    }
  };
  const changed = () => {
    load();
    window.dispatchEvent(new CustomEvent(ALERTS_CHANGED_EVENT));
  };
  const openCount = all ? all.filter((a) => a.status !== "resolved" && !a.dismissed).length : 0;

  return (
    <div className="alerts-page">
      <div className="page-header">
        <h1>Alerts</h1>
      </div>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      <div className="card">
        <div className="log-source-tabs alert-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={"log-source" + (tab === t.key ? " on" : "")} onClick={() => setTab(t.key)}>
              {t.label}
              {t.key === "inbox" && openCount > 0 && <span className="alert-tab-count">{openCount}</span>}
            </button>
          ))}
        </div>
        <AlertList all={all} types={types} history={tab === "history"} admin={admin} onOpen={(a) => setOpen(a)} />
      </div>
      {open && <AlertDetail id={open.id} canAct={canAct} onClose={closeDetail} onChanged={changed} />}
    </div>
  );
};

export default Alerts;
